import type { ApiClient } from '@carbonwebmail/api-client'
import type {
  ContactFieldDto,
  ImportJobDto,
  MondayBoardDto,
  MondaySyncDto,
} from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { Checkbox } from '../../ui/Checkbox.js'
import { Field } from '../../ui/Field.js'
import { count } from '../../ui/format.js'
import { PillButton } from '../../ui/PillButton.js'
import { Problems } from '../../ui/Problems.js'
import { Table, Td, Tr } from '../../ui/Table.js'
import { Importing } from './Importing.js'
import type { MondayTarget } from './useMondayImport.js'
import { useMondayImport } from './useMondayImport.js'

/**
 * The option that makes a column rather than choosing one.
 *
 * A sentinel rather than a real target: picking it does not set a mapping, it
 * declares a field and then maps to that.
 */
const NEW = 'new-column'

/** How many boards the picker draws before asking somebody to narrow it. */
const SHOWN = 50

/**
 * Importing contacts from a monday.com board.
 *
 * Connect, pick a board, say what each column is, import. One screen because it
 * is one errand, and each answer decides what the next step can offer.
 *
 * **monday is an import source and nothing else.** `CLAUDE.md` §3 is explicit:
 * never a live read path, never a source of truth. Nothing here writes back to
 * monday, and a contact that arrives is an ordinary contact — the same rows,
 * the same consent record, the same duplicate handling a CSV gets, because it
 * goes through the same importer.
 */
export function MondayPage({ client }: { readonly client: ApiClient }) {
  const monday = useMondayImport({ client })

  return (
    <>
      <header className="mb-5">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Import from monday.com</h1>
        <p className="mt-1 text-[13px] text-t2">
          Connect an account, choose a board, and say what each column is. The people it adds are
          ordinary contacts — nothing is written back to monday.
        </p>
      </header>

      {monday.problem === undefined ? null : <Alert>{monday.problem}</Alert>}

      <Step monday={monday} />
    </>
  )
}

/**
 * Whichever step is current.
 *
 * Its own component because the page then reads as a heading, a problem and a
 * step — four conditions inline made that something to work out rather than
 * read, which is what Biome's complexity rule is for.
 */
function Step({ monday }: { readonly monday: ReturnType<typeof useMondayImport> }) {
  if (!monday.loaded) return <p className="text-[13px] text-t3">Loading…</p>

  if (monday.step === 'connect') {
    return <Connect busy={monday.busy} onConnect={monday.connect} />
  }

  if (monday.step === 'board') {
    return (
      <Boards
        account={monday.connection?.account.name ?? ''}
        boards={monday.boards}
        busy={monday.busy}
        listed={monday.listed}
        onChoose={monday.choose}
        onDisconnect={monday.disconnect}
      />
    )
  }

  if (monday.step === 'map' && monday.board !== undefined) {
    return (
      <MapColumns
        board={monday.board}
        busy={monday.busy}
        fields={monday.fields}
        groupsAsSegments={monday.groupsAsSegments}
        mapping={monday.mapping}
        onBack={monday.again}
        onCreate={monday.createField}
        onGroupsAsSegments={monday.setGroupsAsSegments}
        onImport={() => void monday.run()}
        onTarget={monday.target}
        problems={monday.problems}
      />
    )
  }

  if (monday.step === 'importing') return <Importing job={monday.job} />

  if (monday.job === undefined) return null
  return (
    <Done
      job={monday.job}
      making={monday.busy}
      onAgain={monday.again}
      onNewWebhook={monday.newWebhook}
      onSync={monday.setSyncAction}
      sync={monday.sync}
      webhookUrl={monday.webhookUrl}
    />
  )
}

/**
 * The token.
 *
 * A personal API token rather than an OAuth round trip, which is what the owner
 * asked for and what works today. It is proved against monday before it is
 * stored, so a connection that exists is one that worked at least once.
 */
function Connect({
  busy,
  onConnect,
}: {
  readonly busy: boolean
  readonly onConnect: (token: string) => Promise<void>
}) {
  const [token, setToken] = useState('')

  return (
    <section className="max-w-xl rounded-card border border-line bg-surface p-5">
      <h2 className="mb-1.5 font-bold text-[15px] text-t1">Connect monday</h2>
      <p className="mb-4 text-[13px] text-t2 leading-relaxed">
        In monday: your avatar, then <strong className="text-t1">Developers</strong> →{' '}
        <strong className="text-t1">My access tokens</strong>. Copy the token and paste it here. It
        is stored encrypted and never shown again.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          void onConnect(token.trim())
        }}
      >
        <Field
          disabled={busy}
          id="monday-token"
          label="Personal API token"
          onChange={setToken}
          type="password"
          value={token}
        />
        <div className="mt-3">
          <PillButton disabled={busy || token.trim() === ''} type="submit" variant="primary">
            {busy ? 'Checking…' : 'Connect'}
          </PillButton>
        </div>
      </form>
    </section>
  )
}

function Boards({
  account,
  boards,
  busy,
  listed,
  onChoose,
  onDisconnect,
}: {
  readonly account: string
  readonly boards: readonly { boardId: string; name: string }[]
  readonly busy: boolean
  readonly listed: boolean
  readonly onChoose: (boardId: string) => Promise<void>
  readonly onDisconnect: () => Promise<void>
}) {
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-card border border-line bg-surface-2 px-4 py-3">
        <p className="text-[13px] text-t2">
          Connected as <strong className="font-semibold text-t1">{account}</strong>
        </p>
        <button
          className="font-medium text-[13px] text-t3 hover:text-danger"
          disabled={busy}
          onClick={() => void onDisconnect()}
          type="button"
        >
          Disconnect
        </button>
      </div>

      {/*
       * Only once the list actually came back. It used to say this whenever
       * `boards` was empty, which included the case where the request had
       * failed — so a timeout reported an account with no boards, under a
       * banner saying monday could not be reached. Two contradictory answers,
       * and the confident one was wrong.
       */}
      {boards.length === 0 ? (
        listed ? (
          <p className="rounded-card border border-line bg-surface p-10 text-center text-[13px] text-t2">
            That account has no boards we can read.
          </p>
        ) : null
      ) : (
        <BoardTable boards={boards} busy={busy} onChoose={onChoose} />
      )}
    </>
  )
}

/**
 * The boards, with a box to narrow them.
 *
 * **Because the first real account had 4,631 of them.** A table that long is
 * not a picker, it is a scroll, and the board somebody wants is one they can
 * name. Filtering happens here rather than at monday because monday's `boards`
 * query has no name filter, and because the names are already loaded: typing
 * costs nothing and matches as you go.
 *
 * Capped at what fits a glance. Somebody with thousands of boards types a word
 * rather than scrolling to the end, and rendering all of them makes the page
 * slow at the one moment it should feel quick.
 */
function BoardTable({
  boards,
  busy,
  onChoose,
}: {
  readonly boards: readonly { boardId: string; name: string }[]
  readonly busy: boolean
  readonly onChoose: (boardId: string) => Promise<void>
}) {
  const [search, setSearch] = useState('')

  const looking = search.trim().toLowerCase()
  const matching =
    looking === '' ? boards : boards.filter((one) => one.name.toLowerCase().includes(looking))
  const shown = matching.slice(0, SHOWN)

  return (
    <>
      <div className="mb-3">
        <Field
          hint={`${count(boards.length)} boards on this account.`}
          id="monday-board-search"
          label="Find a board"
          onChange={setSearch}
          type="text"
          value={search}
        />
      </div>

      {matching.length === 0 ? (
        <p className="rounded-card border border-line bg-surface p-10 text-center text-[13px] text-t2">
          No board here is called that.
        </p>
      ) : (
        <>
          <Table head={['Board', '']} minWidth={640}>
            {shown.map((board) => (
              <Tr key={board.boardId}>
                <Td strong>{board.name}</Td>
                <Td>
                  <span className="flex justify-end">
                    <button
                      className="font-semibold text-[13px] text-brand"
                      disabled={busy}
                      onClick={() => void onChoose(board.boardId)}
                      type="button"
                    >
                      Choose
                    </button>
                  </span>
                </Td>
              </Tr>
            ))}
          </Table>

          {matching.length > shown.length ? (
            <p className="mt-3 text-[13px] text-t3">
              Showing {count(shown.length)} of {count(matching.length)}. Narrow the search to see
              the rest.
            </p>
          ) : null}
        </>
      )}
    </>
  )
}

/**
 * What each column becomes.
 *
 * Every column is listed, including the ones being skipped — a mapping screen
 * that hid them would make "I did not map that" and "that column is not there"
 * look the same.
 */
function MapColumns({
  board,
  busy,
  fields,
  mapping,
  problems,
  groupsAsSegments,
  onCreate,
  onTarget,
  onGroupsAsSegments,
  onImport,
  onBack,
}: {
  readonly board: MondayBoardDto
  readonly busy: boolean
  readonly fields: readonly ContactFieldDto[]
  readonly mapping: Readonly<Record<string, MondayTarget>>
  readonly problems: readonly string[]
  readonly groupsAsSegments: boolean
  /** Declares a contact field named after the column, and maps to it. */
  readonly onCreate: (column: { columnId: string; title: string; type: string }) => Promise<void>
  readonly onTarget: (columnId: string, target: MondayTarget) => void
  readonly onGroupsAsSegments: (on: boolean) => void
  readonly onImport: () => void
  readonly onBack: () => void
}) {
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-bold text-[15px] text-t1">
          {board.name} <span className="font-medium text-t3">— {count(board.items)} items</span>
        </h2>
        <button
          className="font-medium text-[13px] text-t3 hover:text-t1"
          onClick={onBack}
          type="button"
        >
          Choose another board
        </button>
      </div>

      <Problems problems={problems} />

      <Table head={['monday column', 'Type', 'Becomes']} minWidth={640}>
        {board.columns.map((column) => (
          <Tr key={column.columnId}>
            <Td strong>{column.title}</Td>
            <Td>{column.type}</Td>
            <Td>
              <select
                aria-label={`What ${column.title} becomes`}
                className="rounded-field border border-line bg-surface px-2.5 py-1.5 text-[13px] text-t1"
                disabled={busy}
                onChange={(event) => {
                  if (event.target.value === NEW) return void onCreate(column)
                  onTarget(column.columnId, parseTarget(event.target.value))
                }}
                value={selected(mapping[column.columnId])}
              >
                <option value="skip">Skip</option>
                <option value="email">Email address</option>
                <option value="name">Name</option>
                {/*
                 * Above the declared columns, because it is not one of them:
                 * this column decides who gets mail. A board that records an
                 * unsubscribe and an import that files it as ordinary text is
                 * how somebody who opted out receives a campaign.
                 */}
                <option value="subscription">Subscribed or not</option>
                {fields.map((field) => (
                  <option key={field.key} value={`field:${field.key}`}>
                    {field.label}
                  </option>
                ))}
                {/*
                 * Last, and the reason the rest of this list can be short. On an
                 * organisation that has declared nothing there are no fields
                 * above, so a board with a Subscription column offered three
                 * answers — skip it, call it the address, or call it the name.
                 * This makes the column, names it after the monday column and
                 * maps to it in one go.
                 */}
                <option value={NEW}>Create a new column…</option>
              </select>
            </Td>
          </Tr>
        ))}
      </Table>

      <div className="mt-4 rounded-card border border-line bg-surface p-4">
        <span className="flex items-start gap-2.5">
          <Checkbox
            checked={groupsAsSegments}
            id="monday-groups"
            label="Make a segment from each group"
            onChange={() => onGroupsAsSegments(!groupsAsSegments)}
          />
          <label className="cursor-pointer text-[13px] text-t1" htmlFor="monday-groups">
            Make a segment from each group
            <span className="mt-0.5 block text-[12px] text-t3">
              {board.groups.length === 0
                ? 'This board has no groups.'
                : `${board.groups.map((group) => group.title).join(', ')} — everyone in a group is connected to a segment of the same name. A segment you already have with that name is used rather than a second one.`}
            </span>
          </label>
        </span>
      </div>

      <div className="mt-4">
        <PillButton
          disabled={busy || problems.length > 0}
          onClick={onImport}
          type="button"
          variant="primary"
        >
          {busy ? 'Importing…' : 'Import this board'}
        </PillButton>
      </div>
    </>
  )
}

function Done({
  job,
  sync,
  making,
  webhookUrl,
  onSync,
  onNewWebhook,
  onAgain,
}: {
  readonly job: ImportJobDto
  readonly sync: MondaySyncDto
  readonly making: boolean
  /** Minted since the import, when somebody asked. Shown the same way. */
  readonly webhookUrl: string | undefined
  readonly onSync: (next: MondaySyncDto) => Promise<void>
  readonly onNewWebhook: () => Promise<void>
  readonly onAgain: () => void
}) {
  return (
    <section className="rounded-card border border-line bg-surface p-5">
      <h2 className="mb-3 font-bold text-[15px] text-t1">
        {job.boardName ?? 'That board'} imported
      </h2>

      <dl className="mb-4 grid gap-3 sm:grid-cols-4">
        <Figure label="Added" value={job.added} />
        <Figure label="Already there" value={job.duplicates} />
        <Figure label="No address" value={job.withoutEmail} />
        <Figure label="Unsubscribed" value={job.unsubscribed} />
        <Figure label="Rejected" value={job.rejectedTotal} />
      </dl>

      {job.withoutEmail === 0 ? null : (
        <Alert tone="unknown">
          {count(job.withoutEmail)} items had nothing in the column you mapped to the address, so
          they were left out. If that is most of the board, the wrong column was mapped.
        </Alert>
      )}

      {/*
       * Shown because the reading is a guess: a board's own word for a
       * subscription is not from any vocabulary, so anything unrecognised is
       * read as unsubscribed. Safe, and wrong in a way that would otherwise
       * be silent — this is the line that shows it.
       */}
      {job.unsubscribed === 0 ? null : (
        <Alert tone="unknown">
          {count(job.unsubscribed)} of them are marked as not subscribed and will not be sent to. If
          that is more than you expected, the words in that column were not ones we recognise —
          anything we cannot read is treated as unsubscribed on purpose.
        </Alert>
      )}

      {job.segments.length === 0 ? null : (
        <p className="mb-4 text-[13px] text-t2">
          Segments: <strong className="font-semibold text-t1">{job.segments.join(', ')}</strong>
        </p>
      )}

      <KeepInStep
        making={making}
        onNewWebhook={onNewWebhook}
        onSync={onSync}
        sync={sync}
        webhookUrl={webhookUrl ?? null}
      />

      <div className="mt-4">
        <PillButton onClick={onAgain} type="button" variant="secondary">
          Import another board
        </PillButton>
      </div>
    </section>
  )
}

/**
 * Keeping the board and the contacts in step, after the one-time import.
 *
 * **monday pushes; this never polls.** You paste the URL into a monday
 * automation, monday calls it when something happens, and the same import and
 * the same delete run as if somebody had done it here. Nothing is written back
 * to monday, which is `CLAUDE.md` §3's line and the reason this stays an
 * integration rather than becoming a chassis.
 *
 * **Both switches start off, and deleting says what it does.** Creating a
 * contact is additive; deleting one removes them and their segment
 * memberships, and a board being tidied looks exactly like a board being
 * corrected. Only contacts this board actually imported can be deleted — the
 * link recorded at import is the whole safeguard.
 *
 * The URL appears once. Only its fingerprint is stored, so it cannot be shown
 * again; importing the board again mints a new one.
 */
function KeepInStep({
  webhookUrl,
  sync,
  making,
  onSync,
  onNewWebhook,
}: {
  readonly webhookUrl: string | null
  readonly sync: MondaySyncDto
  readonly making: boolean
  readonly onSync: (next: MondaySyncDto) => Promise<void>
  readonly onNewWebhook: () => Promise<void>
}) {
  return (
    <section className="mt-5 border-line border-t pt-5">
      <h3 className="font-bold text-[14px] text-t1">Keep it in step</h3>
      <p className="mt-1 mb-3 text-[13px] text-t2 leading-relaxed">
        In monday: <strong className="text-t1">Automations</strong> → when an item is created (or
        deleted) → <strong className="text-t1">send a webhook</strong>. Paste this URL.
      </p>

      {webhookUrl === null ? (
        <div className="mb-3 rounded-field border border-line bg-surface-2 px-3 py-2.5">
          <p className="text-[13px] text-t2">
            This board’s URL was shown once, when it was first made, and only its fingerprint is
            kept. Make a new one if you do not have it —{' '}
            <strong className="text-t1">the old URL stops working</strong>, so anything already
            pasted into monday must be replaced.
          </p>
          <div className="mt-2.5">
            <PillButton
              disabled={making}
              onClick={() => void onNewWebhook()}
              type="button"
              variant="secondary"
            >
              {making ? 'Making…' : 'Make a new webhook URL'}
            </PillButton>
          </div>
        </div>
      ) : (
        <>
          {/* Readonly rather than text so it can be selected in one gesture. */}
          <input
            aria-label="Webhook URL"
            className="mb-2 w-full rounded-field border border-line bg-surface-2 px-3 py-2 font-mono text-[12px] text-t1"
            onFocus={(event) => event.currentTarget.select()}
            readOnly
            value={webhookUrl}
          />
          <p className="mb-3 text-[12px] text-t3">Copy it now — it is not shown again.</p>
        </>
      )}

      <div className="flex flex-col gap-2.5">
        <Switch
          checked={sync.onItemCreated}
          hint="Adds a contact. An address already on your list is left exactly as it is."
          id="monday-on-created"
          label="When an item is created in monday"
          onChange={(on) => void onSync({ ...sync, onItemCreated: on })}
        />
        <Switch
          checked={sync.onItemDeleted}
          danger
          hint="Deletes that contact and its segment memberships. Only contacts this board imported, and there is no undo. What was already sent to them is kept."
          id="monday-on-deleted"
          label="When an item is deleted in monday"
          onChange={(on) => void onSync({ ...sync, onItemDeleted: on })}
        />
      </div>
    </section>
  )
}

function Switch({
  id,
  label,
  hint,
  checked,
  danger,
  onChange,
}: {
  readonly id: string
  readonly label: string
  readonly hint: string
  readonly checked: boolean
  readonly danger?: boolean
  readonly onChange: (on: boolean) => void
}) {
  return (
    <span className="flex items-start gap-2.5">
      <Checkbox checked={checked} id={id} label={label} onChange={() => onChange(!checked)} />
      <label className="cursor-pointer text-[13px] text-t1" htmlFor={id}>
        {label}
        <span className={`mt-0.5 block text-[12px] ${danger === true ? 'text-danger' : 'text-t3'}`}>
          {hint}
        </span>
      </label>
    </span>
  )
}

function Figure({ label, value }: { readonly label: string; readonly value: number }) {
  return (
    <div className="rounded-field border border-line bg-surface-2 px-3 py-2.5">
      <dt className="font-semibold text-[11px] text-t3 uppercase tracking-[0.06em]">{label}</dt>
      <dd className="mt-0.5 font-bold font-num text-[20px] text-t1 tabular-nums">{count(value)}</dd>
    </div>
  )
}

/** The select speaks in strings; the mapping speaks in targets. One place each way. */
function selected(target: MondayTarget | undefined): string {
  if (target === undefined || target.kind === 'skip') return 'skip'
  return target.kind === 'field' ? `field:${target.key}` : target.kind
}

function parseTarget(value: string): MondayTarget {
  if (value === 'email' || value === 'name' || value === 'subscription') return { kind: value }
  if (value.startsWith('field:')) return { kind: 'field', key: value.slice('field:'.length) }
  return { kind: 'skip' }
}
